import _ from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { svg } from '../assets/svg';
import { components } from "../components";
import constants from '../helpers/constants';
import Enums from '../helpers/enums';
import utils from '../helpers/utils';
import { hooks } from '../hooks';
import { Routes } from '../routes';
import { getCustomizeItems } from '../screens/CustomizeHelper';
import { RootState } from '../store';
import { removeFromCart, updateToCart } from '../store/slices/cartSlice';

type Props = {
  item: any;
  index: number;
  isLast: boolean;
};

export const OrderItem: React.FC<Props> = ({ item, index, isLast }) => {
  const navigate = hooks.useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state: RootState) => state.cartReducer.cartItems);
  const config = useSelector((state: RootState) => state.appReducer.config);
  const selectedSiteId = useSelector((state: RootState) => state.selectedSiteReducer.selectedSiteId);
  const allSites = useSelector((state: RootState) => state.screenReducer.sites);
  const isDelivery = useSelector((state: RootState) => state.cartReducer.isDelivery);
  const itemCustomizations = useSelector((state: RootState) => state.lookupReducer.itemCustomizations);

  const selectedSite = utils.getSelectedSite(selectedSiteId, allSites);
  const [errors, setErrors] = React.useState<{ [key: number]: string }>({});

  const maxQuantity = React.useMemo(() => {
    const totalQuantity = _.sumBy(cartItems, (currentItem: any) =>
      currentItem.menuItemId === item.menuItemId ? currentItem.quantity : 0
    );
    return item.maxQty && totalQuantity > 0
      ? Math.min(item.maxQty - (totalQuantity - item.quantity), item.maxQty)
      : 20;
  }, [cartItems, item]);

  const handleLimitReached = (isMax: boolean, idx: number) => {
    if (isMax) {
      setErrors(prev => ({ ...prev, [idx]: 'Maximum quantity reached.' }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[idx];
        return newErrors;
      });
    }
  };

  const calculatePrice = (item: any) => {
    if (item.type === Enums.comboType.quickCombo) {
      return utils.formatCurrency(
        (item.price + item.totalCutomizedItemPrice + item.swappedPrice) * item.quantity
      );
    } else {
      let itemPrice = (item.price + item.totalCutomizedItemPrice) * item.quantity;
      if (item.options && item.options.length > 0) {
        itemPrice += item.options[0].price * item.quantity;
      }
      return utils.formatCurrency(itemPrice);
    }
  };

  const renderCartPrice = (item: any) => {
    if (item.isFree) {
      if (item.customizations && item.customizations.length > 0) {
        return calculatePrice(item);
      }
      return 'Free';
    } else {
      return calculatePrice(item);
    }
  };

  const updateQuantity = (newQty: number) => {
    if (newQty > maxQuantity) {
      handleLimitReached(true, index);
      return;
    }
    handleLimitReached(false, index);

    if (newQty === 0) {
      dispatch(removeFromCart(item._id));
      return;
    }

    const tmpArray = _.cloneDeep(cartItems);
    const foundItem = _.find(tmpArray, { _id: item._id });
    if (!foundItem) return;
    foundItem.quantity = newQty;
    dispatch(updateToCart(foundItem));
  };

  const onPressCustomize = async (item: any) => {
    await getCustomizeItems({
      item: item,
      siteId: selectedSite.siteId,
      isDelivery: isDelivery,
      dispatch: dispatch,
      itemCustomizations: itemCustomizations,
      navigate: navigate,
      isMandatoryCustomization: item.isMandatory || false,
      showSkip: true,
      isUpdateCart: true
    });
  };

  const renderCustomizeItems = (customizeItems: any) => {
    if (_.size(customizeItems) === 0) {
      return null;
    }
    return (
      <>
        {customizeItems.map((customItem: any, index: any) => {
          if (customItem.label) {
            return (
              <div key={index}>
                <span
                  className="t10"
                  style={{
                    color: customItem.label.includes('Removed')
                      ? 'var(--main-turquoise)'
                      : 'var(--text-color)',
                  }}
                >
                  {customItem.label}
                </span>
              </div>
            );
          }
        })}
      </>
    );
  };

  return (
    <>
      {errors[index] && (
        <span
          className="t12"
          style={{
            color: 'red',
            marginBottom: 5,
            marginRight: 10,
            display: 'block',
            justifyContent: 'end',
            textAlign: 'right',
          }}
        >
          {errors[index]}
        </span>
      )}

      <li
        style={{
          backgroundColor: 'var(--white-color)',
          borderRadius: 10,
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          marginBottom: isLast ? 0 : 14,
          boxShadow: "0px 3px 5px rgba(0,0,0,0.1)",
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start', // align top
            gap: 14,
          }}
        >
          <img
            src={utils.getImagePath(item.imagePath)}
            alt={item.name}
            style={{ width: 80, height: 77, borderRadius: 10, flexShrink: 0 }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <span className="t14" style={{ fontWeight: 500 }}>
              {item.name}
            </span>
            {(!item.options || item.options.length === 0) && (
              <span
                className="t12"
                onClick={() => onPressCustomize(item)}
                style={{
                  color: 'var(--main-turquoise)',
                  cursor: 'pointer',
                  marginTop: 2,
                  marginBottom: 2,
                  fontWeight: 500,
                  textDecoration: 'underline',
                }}
              >
                Customise
              </span>
            )}
            <span className="t14" style={{ color: 'var(--main-color)', fontWeight: 400 }}>
              {renderCartPrice(item)}{' '}
              {config && config.canShowCalory && item.caloricValue
                ? `${constants.unicode.bull} ${item.caloricValue}kJ`
                : null}
            </span>

            <div style={{ marginTop: 6 }}>{renderCustomizeItems(item.type === Enums.comboType.quickCombo
              ? item.items[0].customizations
              : item.customizations)}</div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginLeft: 10,
            }}
          >
            <button
              style={{ padding: '10px 14px 4px 14px', borderRadius: 4 }}
              onClick={(event) => {
                event.stopPropagation();
                updateQuantity(item.quantity - 1);
              }}
            >
              <svg.RemoveSvg />
            </button>
            <span className="t12" style={{ lineHeight: 1 }}>
              {item.quantity}
            </span>
            <button
              style={{ padding: '4px 14px 14px 14px', borderRadius: 4 }}
              onClick={(event) => {
                event.stopPropagation();
                updateQuantity(item.quantity + 1);
              }}
            >
              <svg.AddSvg />
            </button>
          </div>
        </div>
      </li>
    </>
  );
};